import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Truck, MapPinLine, MagnifyingGlass, Path, ArrowRight, NavigationArrow,
  Car, Users, Clock, ListBullets, MapTrifold, CaretDown,
} from '@phosphor-icons/react';

import LogisticsLayout from './LogisticsLayout';
import { useLogisticsExercise } from '../../context/LogisticsExerciseContext';
import VenueMap, { VenueDetailCard } from './VenueMap';
import VenueCard from '../../components/logistics/VenueCard';
import TransportFilters from '../../components/logistics/TransportFilters';
import DepotDetailPanel from '../../components/logistics/DepotDetailPanel';
import TransportBookingDrawer from '../../components/logistics/TransportBookingDrawer';
import logisticsApi from '../../lib/logisticsApi';
import { useWebSocket } from '../../context/WebSocketContext';

const PRESET_LOCATIONS = [
  { name: 'London',     center: [-0.13, 51.51],     zoom: 13, image: 'https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?w=600&h=400&fit=crop', country: 'United Kingdom' },
  { name: 'Manchester', center: [-2.244, 53.483],    zoom: 13, image: 'https://images.unsplash.com/photo-1515586838455-8f8f940d6853?w=600&h=400&fit=crop', country: 'United Kingdom' },
  { name: 'Edinburgh',  center: [-3.19, 55.95],      zoom: 13, image: 'https://images.unsplash.com/photo-1477959858617-67f85cf4f1df?w=600&h=400&fit=crop', country: 'Scotland' },
  { name: 'New York',   center: [-73.985, 40.748],   zoom: 13, image: 'https://images.unsplash.com/photo-1496442226666-8d4d0e62e6e9?w=600&h=400&fit=crop', country: 'United States' },
  { name: 'Tokyo',      center: [139.755, 35.68],    zoom: 13, image: 'https://images.unsplash.com/photo-1540959733332-eab4deabeeaf?w=600&h=400&fit=crop', country: 'Japan' },
  { name: 'Sydney',     center: [151.209, -33.865],  zoom: 13, image: 'https://images.unsplash.com/photo-1506973035872-a4ec16b8e8d9?w=600&h=400&fit=crop', country: 'Australia' },
];

const fmt = (minor) =>
  ((minor || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const TransportContent = () => {
  const { getSocket } = useWebSocket();
  const { exercises, selected: ctxExercise, selectedId: exerciseId } = useLogisticsExercise();
  const exercise = ctxExercise;
  const currencySymbol = exercise?.currency_symbol || 'Ÿ';

  // Wallets
  const [wallets, setWallets] = useState([]);
  const loadWallets = useCallback(async () => {
    if (!exerciseId) { setWallets([]); return; }
    try {
      const w = await logisticsApi.myWallets();
      const all = [...(w.personal || []), ...(w.team || [])];
      setWallets(all.filter((x) => x.logistics_exercise_id === exerciseId && !x.frozen));
    } catch { setWallets([]); }
  }, [exerciseId]);
  useEffect(() => { loadWallets(); }, [loadWallets]);

  // Journey planner state
  const [originQuery, setOriginQuery] = useState('');
  const [destQuery, setDestQuery] = useState('');
  const [originSuggestions, setOriginSuggestions] = useState([]);
  const [destSuggestions, setDestSuggestions] = useState([]);
  const [showOriginSuggestions, setShowOriginSuggestions] = useState(false);
  const [showDestSuggestions, setShowDestSuggestions] = useState(false);
  const [origin, setOrigin] = useState(null);
  const [destination, setDestination] = useState(null);
  const [routeEstimate, setRouteEstimate] = useState(null);
  const [routeLoading, setRouteLoading] = useState(false);
  const originRef = React.useRef(null);
  const destRef = React.useRef(null);
  const originDebounce = React.useRef(null);
  const destDebounce = React.useRef(null);
  const skipOriginAC = React.useRef(false);
  const skipDestAC = React.useRef(false);
  const [geocodeLoading, setGeocodeLoading] = useState({ origin: false, dest: false });

  // Depot browse state
  const [browseVenues, setBrowseVenues] = useState([]);
  const [browseLoading, setBrowseLoading] = useState(false);
  const [browseTotal, setBrowseTotal] = useState(0);
  const [browseOffset, setBrowseOffset] = useState(0);
  const [browseError, setBrowseError] = useState(null);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const [activeCity, setActiveCity] = useState('London');
  const [presetCities, setPresetCities] = useState([]);

  // Filters
  const [vehicleTypeFilter, setVehicleTypeFilter] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [passengerCount, setPassengerCount] = useState(null);
  const [viewMode, setViewMode] = useState('cards');

  // Panels
  const [detailPanelVenue, setDetailPanelVenue] = useState(null);
  const [bookingVenue, setBookingVenue] = useState(null);
  const [bookingVehicle, setBookingVehicle] = useState(null);
  const [selectedVenue, setSelectedVenue] = useState(null);
  const [mapKey, setMapKey] = useState(0);
  const [center, setCenter] = useState(PRESET_LOCATIONS[0].center);
  const [zoom, setZoom] = useState(PRESET_LOCATIONS[0].zoom);

  useEffect(() => {
    if (!exerciseId) return;
    logisticsApi.presetCities(exerciseId)
      .then((r) => setPresetCities(r.cities || []))
      .catch(() => setPresetCities(PRESET_LOCATIONS.map((l) => l.name)));
  }, [exerciseId]);

  // Geocode autocomplete for origin
  useEffect(() => {
    if (skipOriginAC.current) { skipOriginAC.current = false; return; }
    if (!originQuery || originQuery.length < 2 || !exerciseId) { setOriginSuggestions([]); return; }
    if (originDebounce.current) clearTimeout(originDebounce.current);
    originDebounce.current = setTimeout(async () => {
      setGeocodeLoading((p) => ({ ...p, origin: true }));
      try {
        const res = await logisticsApi.geocodeSearch(exerciseId, originQuery);
        setOriginSuggestions(res.suggestions || []);
        setShowOriginSuggestions(true);
      } catch { setOriginSuggestions([]); }
      finally { setGeocodeLoading((p) => ({ ...p, origin: false })); }
    }, 800);
    return () => { if (originDebounce.current) clearTimeout(originDebounce.current); };
  }, [originQuery, exerciseId]);

  // Geocode autocomplete for destination
  useEffect(() => {
    if (skipDestAC.current) { skipDestAC.current = false; return; }
    if (!destQuery || destQuery.length < 2 || !exerciseId) { setDestSuggestions([]); return; }
    if (destDebounce.current) clearTimeout(destDebounce.current);
    destDebounce.current = setTimeout(async () => {
      setGeocodeLoading((p) => ({ ...p, dest: true }));
      try {
        const res = await logisticsApi.geocodeSearch(exerciseId, destQuery);
        setDestSuggestions(res.suggestions || []);
        setShowDestSuggestions(true);
      } catch { setDestSuggestions([]); }
      finally { setGeocodeLoading((p) => ({ ...p, dest: false })); }
    }, 800);
    return () => { if (destDebounce.current) clearTimeout(destDebounce.current); };
  }, [destQuery, exerciseId]);

  // Close dropdowns on outside click
  useEffect(() => {
    const handler = (e) => {
      if (originRef.current && !originRef.current.contains(e.target)) setShowOriginSuggestions(false);
      if (destRef.current && !destRef.current.contains(e.target)) setShowDestSuggestions(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const selectOrigin = (s) => {
    skipOriginAC.current = true;
    setOriginQuery(s.name);
    setOrigin({ name: s.name, lat: s.lat, lng: s.lng });
    setShowOriginSuggestions(false);
    setOriginSuggestions([]);
  };

  const selectDestination = (s) => {
    skipDestAC.current = true;
    setDestQuery(s.name);
    setDestination({ name: s.name, lat: s.lat, lng: s.lng });
    setShowDestSuggestions(false);
    setDestSuggestions([]);
  };

  // Estimate route when both points set
  const estimateRouteAction = useCallback(async () => {
    if (!origin || !destination || !exerciseId) return;
    setRouteLoading(true);
    try {
      const est = await logisticsApi.estimateRoute(exerciseId, {
        origin_lat: origin.lat, origin_lng: origin.lng,
        destination_lat: destination.lat, destination_lng: destination.lng,
      });
      setRouteEstimate({ ...est, origin, destination });
    } catch { setRouteEstimate(null); }
    finally { setRouteLoading(false); }
  }, [origin, destination, exerciseId]);

  useEffect(() => {
    if (origin && destination) estimateRouteAction();
  }, [origin, destination, estimateRouteAction]);

  // Map vehicle type filter → depot subcategories
  const vehicleTypeToSubcategories = (vt) => {
    if (!vt) return [];
    const map = {
      sedan: ['car_rental'], suv: ['car_rental'], van: ['car_rental'],
      minibus: ['car_rental'], coach: ['car_rental'],
      rail: ['station'],
    };
    return map[vt] || [];
  };

  // Browse transport depots
  const doBrowse = useCallback(async (append = false, locationOverride = null) => {
    if (!exerciseId) return;
    const city = locationOverride || activeCity;
    if (!city) return;
    setBrowseLoading(true);
    setBrowseError(null);
    try {
      const offset = append ? browseOffset + 24 : 0;
      const subcats = vehicleTypeToSubcategories(vehicleTypeFilter);
      const filters = {
        category: 'transport',
        subcategories: subcats,
        search: searchText || null,
        limit: 24,
        offset,
        cities: [city],
      };
      const res = await logisticsApi.browseVenues(exerciseId, filters);
      if (res.error) {
        setBrowseError(res.error);
        if (!append) setBrowseVenues([]);
        setBrowseTotal(0);
      } else {
        const venues = res.venues || [];
        if (!append) setBrowseVenues(venues);
        else setBrowseVenues((prev) => [...prev, ...venues]);
        setBrowseTotal(res.total || 0);
      }
      setBrowseOffset(offset);
    } catch (e) {
      setBrowseError(e.message);
      if (!append) setBrowseVenues([]);
    } finally { setBrowseLoading(false); }
  }, [exerciseId, activeCity, searchText, browseOffset, vehicleTypeFilter]);

  useEffect(() => {
    if (exerciseId && !initialLoadDone) {
      setInitialLoadDone(true);
      doBrowse(false);
    }
  }, [exerciseId, initialLoadDone, doBrowse]);

  const [pendingBrowse, setPendingBrowse] = useState(false);
  useEffect(() => {
    if (pendingBrowse && exerciseId) {
      setPendingBrowse(false);
      doBrowse(false);
    }
  }, [pendingBrowse, exerciseId, doBrowse, vehicleTypeFilter]);

  const handleCityClick = (cityName) => {
    setActiveCity(cityName);
    setViewMode('cards');
    setBrowseOffset(0);
    setPendingBrowse(true);
    const loc = PRESET_LOCATIONS.find((l) => l.name === cityName);
    if (loc) { setCenter(loc.center); setZoom(loc.zoom); setMapKey((k) => k + 1); }
  };

  // WebSocket
  const upsertWallet = useCallback((wallet) => {
    if (!wallet?.wallet_id) return;
    setWallets((prev) => prev.map((w) => (w.wallet_id === wallet.wallet_id ? { ...w, ...wallet } : w)));
  }, []);

  useEffect(() => {
    const socket = getSocket?.();
    if (!socket) return;
    const onEvent = (data) => {
      if (data?.booking?.logistics_exercise_id && data.booking.logistics_exercise_id !== exerciseId) return;
      upsertWallet(data?.wallet);
    };
    const events = ['ls_transport_booking_created', 'ls_transport_booking_confirmed', 'ls_transport_booking_cancelled',
                    'ls_booking_created', 'ls_booking_confirmed', 'ls_booking_cancelled'];
    events.forEach((e) => socket.on(e, onEvent));
    return () => events.forEach((e) => socket.off(e, onEvent));
  }, [getSocket, exerciseId, upsertWallet]);

  const bookDisabledReason = useMemo(() => {
    if (wallets.length === 0) return 'No spendable wallet for this exercise.';
    if (exercise && !['active', 'draft'].includes(exercise.status))
      return `Exercise '${exercise.name}' is ${exercise.status} — bookings are closed.`;
    return null;
  }, [wallets, exercise]);

  const handleViewDetails = (venue) => setDetailPanelVenue(venue);
  const handleQuickBook = (venue) => setDetailPanelVenue(venue);
  const handleSelectVehicle = (venue, vehicle) => {
    setDetailPanelVenue(null);
    setBookingVenue(venue);
    setBookingVehicle(vehicle);
  };

  const routeInfoForBooking = routeEstimate ? {
    origin: routeEstimate.origin,
    destination: routeEstimate.destination,
    estimated_road_km: routeEstimate.estimated_road_km,
    estimated_duration_minutes: routeEstimate.estimated_duration_minutes,
    originName: routeEstimate.origin?.name,
    destinationName: routeEstimate.destination?.name,
  } : null;

  return (
    <div className="ls-page" data-testid="ls-transport-page">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-xl mb-6" style={{ height: 'clamp(180px, 30vh, 260px)' }}>
        <div className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1449965408869-ebd13bc0a0db?w=1600&h=600&fit=crop)' }} />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to right, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.4) 60%, rgba(0,0,0,0.2) 100%)' }} />
        <div className="relative z-10 h-full flex flex-col justify-end p-6 sm:p-8">
          <div className="flex items-center gap-2 mb-2">
            <Truck size={18} weight="fill" style={{ color: 'var(--ls-primary)' }} />
            <span className="ls-overline tracking-[0.15em]" style={{ color: 'var(--ls-primary)' }}>GROUND TRANSPORT</span>
          </div>
          <h1 className="ls-heading text-2xl sm:text-3xl font-bold text-white mb-1">
            Book your journey
          </h1>
          <p className="text-sm text-white/60 max-w-md">
            Plan routes, hire vehicles, and book transport. Cars, minibuses, coaches, rail and ferry.
          </p>
        </div>
      </div>

      {/* Journey Planner */}
      <div className="ls-elevated rounded-xl p-4 sm:p-5 mb-6" style={{ border: '1px solid var(--ls-border)' }}>
        <div className="flex items-center gap-2 mb-4">
          <Path size={16} weight="bold" style={{ color: 'var(--ls-primary)' }} />
          <h2 className="ls-heading text-sm font-bold">Journey Planner</h2>
          {routeEstimate && (
            <span className="ml-auto text-xs ls-mono font-bold px-2 py-0.5 rounded-full"
              style={{ background: 'var(--ls-primary)', color: '#fff' }}>
              {routeEstimate.estimated_road_km} km · ~{routeEstimate.estimated_duration_minutes} min
            </span>
          )}
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          {/* Origin */}
          <div className="relative flex-1" ref={originRef}>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: 'var(--ls-success)' }} />
              <div className="relative flex-1">
                <MapPinLine size={14} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--ls-muted)' }} />
                <input
                  type="text" className="ls-input w-full !pl-9 !py-2.5 text-sm"
                  placeholder="Where from?"
                  value={originQuery}
                  onChange={(e) => { setOriginQuery(e.target.value); setOrigin(null); setRouteEstimate(null); }}
                  onFocus={() => originSuggestions.length > 0 && setShowOriginSuggestions(true)}
                  data-testid="ls-transport-origin"
                />
                {geocodeLoading.origin && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: 'var(--ls-primary)', borderTopColor: 'transparent' }} />
                )}
              </div>
            </div>
            {showOriginSuggestions && originSuggestions.length > 0 && (
              <div className="absolute top-full left-5 right-0 mt-1 z-50 ls-surface shadow-lg rounded-lg overflow-hidden" style={{ border: '1px solid var(--ls-border)' }}>
                {originSuggestions.map((s, i) => (
                  <button key={i} className="w-full text-left px-4 py-2.5 text-sm flex items-center gap-2 transition-colors hover:bg-[var(--ls-elevated)]"
                    onClick={() => selectOrigin(s)}>
                    <MapPinLine size={14} weight="fill" style={{ color: 'var(--ls-success)' }} />
                    <span style={{ color: 'var(--ls-secondary)' }}>{s.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Arrow */}
          <div className="flex items-center justify-center sm:pt-0">
            <ArrowRight size={18} weight="bold" style={{ color: 'var(--ls-muted)' }} className="hidden sm:block" />
          </div>

          {/* Destination */}
          <div className="relative flex-1" ref={destRef}>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: 'var(--ls-danger)' }} />
              <div className="relative flex-1">
                <NavigationArrow size={14} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--ls-muted)' }} />
                <input
                  type="text" className="ls-input w-full !pl-9 !py-2.5 text-sm"
                  placeholder="Where to?"
                  value={destQuery}
                  onChange={(e) => { setDestQuery(e.target.value); setDestination(null); setRouteEstimate(null); }}
                  onFocus={() => destSuggestions.length > 0 && setShowDestSuggestions(true)}
                  data-testid="ls-transport-destination"
                />
                {geocodeLoading.dest && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: 'var(--ls-primary)', borderTopColor: 'transparent' }} />
                )}
              </div>
            </div>
            {showDestSuggestions && destSuggestions.length > 0 && (
              <div className="absolute top-full left-5 right-0 mt-1 z-50 ls-surface shadow-lg rounded-lg overflow-hidden" style={{ border: '1px solid var(--ls-border)' }}>
                {destSuggestions.map((s, i) => (
                  <button key={i} className="w-full text-left px-4 py-2.5 text-sm flex items-center gap-2 transition-colors hover:bg-[var(--ls-elevated)]"
                    onClick={() => selectDestination(s)}>
                    <NavigationArrow size={14} weight="fill" style={{ color: 'var(--ls-danger)' }} />
                    <span style={{ color: 'var(--ls-secondary)' }}>{s.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Route estimate result */}
        {routeLoading && (
          <div className="flex items-center gap-2 mt-3 justify-center py-2">
            <div className="w-4 h-4 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: 'var(--ls-primary)', borderTopColor: 'transparent' }} />
            <span className="text-sm" style={{ color: 'var(--ls-muted)' }}>Calculating route…</span>
          </div>
        )}
        {routeEstimate && !routeLoading && (
          <div className="flex items-center gap-4 mt-3 pt-3" style={{ borderTop: '1px solid var(--ls-border)' }}>
            <div className="flex items-center gap-2">
              <Path size={14} weight="bold" style={{ color: 'var(--ls-primary)' }} />
              <span className="text-sm font-medium" style={{ color: 'var(--ls-secondary)' }}>
                {routeEstimate.estimated_road_km} km
              </span>
              <span className="text-xs" style={{ color: 'var(--ls-muted)' }}>road distance</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock size={14} weight="bold" style={{ color: 'var(--ls-primary)' }} />
              <span className="text-sm font-medium" style={{ color: 'var(--ls-secondary)' }}>
                ~{routeEstimate.estimated_duration_minutes} min
              </span>
            </div>
            <span className="text-xs ml-auto" style={{ color: 'var(--ls-muted)' }}>
              avg {routeEstimate.avg_speed_kmh} km/h
            </span>
          </div>
        )}
      </div>

      {/* City selector */}
      <div className="mb-4">
        <h2 className="ls-heading text-sm font-bold mb-2">Browse depots by city</h2>
        <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-1">
          {(presetCities.length > 0 ? presetCities.slice(0, 8) : PRESET_LOCATIONS.map((l) => l.name)).map((city) => (
            <button
              key={city}
              className="px-3 py-1.5 rounded-full text-xs font-semibold flex-shrink-0 transition-colors"
              style={{
                background: activeCity === city ? 'var(--ls-primary)' : 'var(--ls-elevated)',
                color: activeCity === city ? '#fff' : 'var(--ls-muted)',
                border: `1px solid ${activeCity === city ? 'var(--ls-primary)' : 'var(--ls-border)'}`,
              }}
              onClick={() => handleCityClick(city)}
            >
              {city}
            </button>
          ))}
        </div>
      </div>

      {/* Filters */}
      {(browseVenues.length > 0 || browseLoading) && (
        <TransportFilters
          vehicleType={vehicleTypeFilter}
          onVehicleTypeChange={(vt) => { setVehicleTypeFilter(vt); setBrowseOffset(0); setPendingBrowse(true); }}
          searchText={searchText}
          onSearchChange={setSearchText}
          passengerCount={passengerCount}
          onPassengerCountChange={setPassengerCount}
          onApply={() => { setBrowseOffset(0); doBrowse(false); }}
          onClear={() => { setSearchText(''); setVehicleTypeFilter(null); setPassengerCount(null); setBrowseOffset(0); doBrowse(false); }}
          loading={browseLoading}
        />
      )}

      {/* Results toolbar */}
      {(browseVenues.length > 0 || browseLoading) && (
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs" style={{ color: 'var(--ls-muted)' }}>
            {browseTotal > 0 && `${browseTotal} depot${browseTotal !== 1 ? 's' : ''} found`}
            {activeCity && <span> in <strong>{activeCity}</strong></span>}
          </p>
          <div className="flex items-center gap-1">
            <button className="p-1.5 rounded-md transition-colors"
              style={{ background: viewMode === 'cards' ? 'var(--ls-primary)' : 'var(--ls-elevated)', color: viewMode === 'cards' ? '#fff' : 'var(--ls-muted)' }}
              onClick={() => setViewMode('cards')} title="Card view">
              <ListBullets size={14} weight="bold" />
            </button>
            <button className="p-1.5 rounded-md transition-colors"
              style={{ background: viewMode === 'map' ? 'var(--ls-primary)' : 'var(--ls-elevated)', color: viewMode === 'map' ? '#fff' : 'var(--ls-muted)' }}
              onClick={() => setViewMode('map')} title="Map view">
              <MapTrifold size={14} weight="bold" />
            </button>
          </div>
        </div>
      )}

      {/* Error */}
      {browseError && (
        <div className="ls-elevated p-4 rounded-lg mb-4 text-center">
          <p className="text-sm" style={{ color: 'var(--ls-danger)' }}>{browseError}</p>
          <button className="ls-btn-ghost text-xs mt-2" onClick={() => doBrowse(false)}>Try again</button>
        </div>
      )}

      {/* Content */}
      {viewMode === 'cards' ? (
        <>
          {browseVenues.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" style={{ gridAutoRows: '280px' }}>
              {browseVenues.map((v) => (
                <VenueCard
                  key={v.venue_id}
                  venue={v}
                  currencySymbol={currencySymbol}
                  onViewDetails={handleViewDetails}
                  onBook={handleQuickBook}
                  bookDisabledReason={bookDisabledReason}
                />
              ))}
            </div>
          )}
          {browseVenues.length > 0 && browseVenues.length < browseTotal && (
            <div className="text-center mt-6">
              <button className="ls-btn-ghost text-sm" onClick={() => doBrowse(true)} disabled={browseLoading}>
                {browseLoading ? 'Loading…' : <>Load more <ArrowRight size={12} weight="bold" /></>}
              </button>
            </div>
          )}
          {!browseLoading && browseVenues.length === 0 && !browseError && initialLoadDone && (
            <div className="text-center py-16">
              <Truck size={48} weight="duotone" style={{ color: 'var(--ls-muted)', margin: '0 auto' }} />
              <h3 className="ls-heading text-lg font-bold mt-4">No transport depots found</h3>
              <p className="text-sm mt-1" style={{ color: 'var(--ls-muted)' }}>Try a different city</p>
            </div>
          )}
          {browseLoading && browseVenues.length === 0 && (
            <div className="text-center py-16">
              <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin mx-auto" style={{ borderColor: 'var(--ls-primary)', borderTopColor: 'transparent' }} />
              <p className="text-sm mt-4" style={{ color: 'var(--ls-muted)' }}>Searching transport…</p>
            </div>
          )}
        </>
      ) : (
        <div className="rounded-xl overflow-hidden" style={{ height: 'max(450px, 60vh)', border: '1px solid var(--ls-border)' }}>
          <VenueMap
            key={mapKey} exerciseId={exerciseId} category="transport"
            center={center} zoom={zoom}
            onSelectVenue={setSelectedVenue}
          />
          {selectedVenue && (
            <VenueDetailCard
              venue={selectedVenue} currencySymbol={currencySymbol}
              onBook={() => handleViewDetails(selectedVenue)}
              onClose={() => setSelectedVenue(null)}
              bookDisabledReason={bookDisabledReason}
            />
          )}
        </div>
      )}

      {/* Depot detail panel */}
      <DepotDetailPanel
        open={!!detailPanelVenue}
        onClose={() => setDetailPanelVenue(null)}
        exerciseId={exerciseId}
        venue={detailPanelVenue}
        currencySymbol={currencySymbol}
        onSelectVehicle={handleSelectVehicle}
        bookDisabledReason={bookDisabledReason}
        routeInfo={routeInfoForBooking}
      />

      {/* Transport booking drawer */}
      <TransportBookingDrawer
        open={!!bookingVenue && !!bookingVehicle}
        onClose={() => { setBookingVenue(null); setBookingVehicle(null); }}
        exerciseId={exerciseId}
        venue={bookingVenue}
        vehicle={bookingVehicle}
        routeInfo={routeInfoForBooking}
        wallets={wallets}
        currencySymbol={currencySymbol}
        onBooked={(booking, wallet) => { upsertWallet(wallet); loadWallets(); }}
      />
    </div>
  );
};

const LogisticsTransportPage = () => (
  <LogisticsLayout>
    <TransportContent />
  </LogisticsLayout>
);

export default LogisticsTransportPage;
